import React, { useCallback, useEffect, ReactNode, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../app/store';
import { fetchDashboardStatsAction } from '../requisitions/requisitionSlice'; // Assuming slice is in requisitions
import { formatCurrency } from '../../utils/formatUtils';
import apiClient from '../../services/api';

const DashboardPage = () => {
  const dispatch: AppDispatch = useDispatch();
  const { dashboardStats, isLoadingDashboardStats, dashboardError } = useSelector(
    (state: RootState) => state.requisitions // Assuming dashboard stats are in requisitions slice
  );
  const { user } = useSelector((state: RootState) => state.auth);

  // Check if user is admin or store user
  const userRoleId = (user as any)?.user_role_id;
  const isAdmin = user && (user as any).is_admin;
  const isStoreUser = userRoleId === 1;

  // Helper to handle undefined/null values for currency formatting
  const formatCurrencyWithFallback = (value: number | undefined) => {
    if (value === undefined || value === null) return 'N/A';
    return formatCurrency(value);
  };
  const [selectedAreaCoach, setSelectedAreaCoach] = useState<{ id: number; name: string } | null>(null);
  const [selectedRegionalCoach, setSelectedRegionalCoach] = useState<{ id: number; name: string } | null>(null);
  const [isAreaCoachesOpen, setIsAreaCoachesOpen] = useState(true);
  const [isRegionalCoachesOpen, setIsRegionalCoachesOpen] = useState(true);

  // Jobcard data state
  const [jcLoading, setJcLoading] = useState(false);
  const [jcError, setJcError] = useState<string | null>(null);
  const [jcSummary, setJcSummary] = useState<{ total: number; open: number; closed: number } | null>(null);
  const [jcRegionalCoaches, setJcRegionalCoaches] = useState<Array<{ id: number; name: string; summary: { total: number; open: number; closed: number } }>>([]);
  const [jcAreaCoaches, setJcAreaCoaches] = useState<Array<{ id: number; name: string; summary: { total: number; open: number; closed: number } }>>([]);
  const [jcStores, setJcStores] = useState<Array<{ id: number; name: string; storenumber?: string; open: number; closed: number; total: number; areaCoachId?: number; areaCoachName?: string }>>([]);
  const [storeSortKey, setStoreSortKey] = useState<'name' | 'open' | 'closed' | 'total' | 'pct'>('pct');
  const [storeSortDir, setStoreSortDir] = useState<'asc' | 'desc'>('desc');
  // Store Job Cards filters
  const [storeSearchTerm, setStoreSearchTerm] = useState('');
  const [storeDateRange, setStoreDateRange] = useState('last_6_months');
  const [storeStartDate, setStoreStartDate] = useState('');
  const [storeEndDate, setStoreEndDate] = useState('');
  const storeDatesInvalid = storeDateRange === 'custom' && (!storeStartDate || !storeEndDate || storeStartDate > storeEndDate);
  const [storeStatusFilter, setStoreStatusFilter] = useState<'all' | 'high_performers' | 'low_performers' | 'open_heavy' | 'closed_heavy'>('all');
  const [storePerfRange, setStorePerfRange] = useState<'all' | 'top_25' | 'middle_50' | 'bottom_25'>('all');

  // Expandable store rows: lazy-loaded jobcards per store
  const [expandedStoreId, setExpandedStoreId] = useState<number | null>(null);
  const [storeJobcardsCache, setStoreJobcardsCache] = useState<Record<number, Array<any>>>({});
  const [storeJobcardsLoading, setStoreJobcardsLoading] = useState<Record<number, boolean>>({});
  const [storeJobcardsError, setStoreJobcardsError] = useState<Record<number, string | null>>({});

  const fetchJson = async (path: string, params?: Record<string, any>) => {
    try {
      const res = await apiClient.get(path, { params });
      console.debug('API response', { path, data: res.data });
      return res.data;
    } catch (e: any) {
      console.error('API error', { path, error: e?.response?.data || e?.message });
      throw e;
    }
  };

  // Build date range params shared across all jobcard endpoints
  const buildDateParams = useCallback((extra?: Record<string, any>): Record<string, any> | undefined => {
    if (storeDatesInvalid) return extra ?? undefined;
    const params: Record<string, any> = { ...extra, date_range: storeDateRange };
    if (storeDateRange === 'custom') {
      params.start_date = storeStartDate;
      params.end_date = storeEndDate;
    }
    return params;
  }, [storeDateRange, storeStartDate, storeEndDate, storeDatesInvalid]);

  // Initial load of summary and area coaches, refetch when impersonation or date range changes
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setJcLoading(true);
        setJcError(null);
        const dateParams = buildDateParams();
        const promises: Promise<any>[] = [
          fetchJson('/approver/jobcards/summary', dateParams),
        ];
        if (isAdmin) {
          promises.push(fetchJson('/approver/jobcards/regional-coaches', dateParams));
          promises.push(fetchJson('/approver/jobcards/area-coaches', dateParams));
        } else {
          promises.push(fetchJson('/approver/jobcards/area-coaches', dateParams));
        }
        const [summaryData, maybeRegional, acData] = isAdmin ? await Promise.all(promises) : await Promise.all([...promises, null as any]);
        if (!mounted) return;
        console.debug('Jobcards summary', summaryData);
        if (isAdmin) console.debug('Jobcards regionalCoaches', maybeRegional);
        console.debug('Jobcards areaCoaches', isAdmin ? acData : maybeRegional);
        setJcSummary(summaryData?.summary ?? null);
        if (isAdmin) {
          setJcRegionalCoaches(maybeRegional?.regionalCoaches ?? []);
          setJcAreaCoaches(acData?.areaCoaches ?? []);
        } else {
          setJcAreaCoaches(maybeRegional?.areaCoaches ?? []);
        }
      } catch (e: any) {
        if (!mounted) return;
        setJcError(e?.message || 'Failed to load job cards');
      } finally {
        if (mounted) setJcLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [isAdmin, storeDateRange, storeStartDate, storeEndDate, storeDatesInvalid, buildDateParams]); // Auto-load on mount and when admin status or date range changes

  // When admin selects a Regional Coach, refetch Area Coaches filtered by regionalId
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!isAdmin) return;
      try {
        setJcLoading(true);
        setJcError(null);
        const params = buildDateParams(selectedRegionalCoach?.id ? { regionalId: selectedRegionalCoach.id } : undefined);
        const acData = await fetchJson('/approver/jobcards/area-coaches', params);
        if (!mounted) return;
        setJcAreaCoaches(acData?.areaCoaches ?? []);
      } catch (e: any) {
        if (!mounted) return;
        setJcError(e?.message || 'Failed to load area coaches');
      } finally {
        if (mounted) setJcLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [isAdmin, selectedRegionalCoach?.id, storeDateRange, storeStartDate, storeEndDate, storeDatesInvalid, buildDateParams]);

  // Load stores (filtered by selected AC if present), refetch when impersonation or date range changes
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setJcLoading(true);
        setJcError(null);
        if (storeDatesInvalid) {
          setJcStores([]);
          return;
        }
        const params: any = { date_range: storeDateRange };
        if (storeDateRange === 'custom') {
          params.start_date = storeStartDate;
          params.end_date = storeEndDate;
        }
        // If area coach is selected, only filter by area coach (it already implies the regional)
        // Otherwise, filter by regional coach if selected
        if (selectedAreaCoach?.id) {
          params.areaCoachId = selectedAreaCoach.id;
        } else if (selectedRegionalCoach?.id) {
          params.regionalId = selectedRegionalCoach.id;
        }
        const data = await fetchJson('/approver/jobcards/stores', params);
        console.debug('Jobcards stores', data);
        if (!mounted) return;
        setJcStores(data?.stores ?? []);
      } catch (e: any) {
        if (!mounted) return;
        setJcError(e?.message || 'Failed to load stores');
      } finally {
        if (mounted) setJcLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [selectedAreaCoach, selectedRegionalCoach, storeDateRange, storeStartDate, storeEndDate, storeDatesInvalid]); // Only trigger when impersonation status actually changes

  // Invalidate expanded-row jobcard cache when date range changes (so re-expand refetches)
  useEffect(() => {
    setStoreJobcardsCache({});
    setStoreJobcardsError({});
    setExpandedStoreId(null);
  }, [storeDateRange, storeStartDate, storeEndDate]);

  // Dynamic labels and descriptions based on user role
  const getCardContent = () => {
    if (isAdmin) {
      return {
        pendingTitle: "Pending Approvals",
        pendingDescription: "All requisitions awaiting any approver's action.",
        recentTitle: "Recently Approved",
        recentDescription: "All requisitions approved by any approver this week.",
        valueDescription: "Total value of all pending submitted requisitions across all stores."
      };
    } else if (isStoreUser) {
      return {
        pendingTitle: "Pending Submitted",
        pendingDescription: "Submitted requisitions pending approval for your store.",
        recentTitle: "Recently Approved",
        recentDescription: "Requisitions approved for your store this week.",
        valueDescription: "Total value of pending submitted requisitions for your store."
      };
    } else {
      return {
        pendingTitle: "Pending My Approval",
        pendingDescription: "Requisitions awaiting your action.",
        recentTitle: "Recently Approved",
        recentDescription: "Requisitions approved by you this week.",
        valueDescription: "Total value of pending submitted requisitions from your assigned stores."
      };
    }
  };

  const cardContent = getCardContent();

  const InfoCard = ({
    label,
    value,
    gradient,
    icon,
  }: {
    label: string;
    value: ReactNode;
    gradient: string; // tailwind class e.g., "from-indigo-500 to-purple-600"
    icon?: ReactNode;
  }) => (
    <div className={`shadow-lg rounded-xl p-4 sm:p-6 text-white bg-gradient-to-br ${gradient} transform transition-all duration-200 hover:-translate-y-1 hover:shadow-2xl`}>
      <div className="flex flex-col">
        <div className="flex items-center gap-2 text-white/70 mb-1">
          {icon}
          <span className="text-sm sm:text-base">{label}</span>
        </div>
        <div className="font-bold text-2xl sm:text-4xl leading-tight min-h-[2rem] sm:min-h-[2.5rem] flex items-center">{value}</div>
      </div>
    </div>
  );

  const LoadingSpinner = ({
    size = 'md',
    label,
    variant = 'light',
  }: {
    size?: 'sm' | 'md' | 'lg';
    label?: string;
    variant?: 'light' | 'dark';
  }) => {
    const dimension = size === 'sm' ? 16 : size === 'lg' ? 36 : 24;
    const ringBase = variant === 'light' ? 'border-white/20 border-t-white' : 'border-gray-300 border-t-blue-500';
    const textClass = variant === 'light' ? 'text-white/80' : 'text-gray-600';
    return (
      <div className="flex items-center gap-2">
        <span
          className={`inline-block rounded-full ${ringBase} animate-spin`}
          style={{ width: dimension, height: dimension, borderWidth: Math.max(2, Math.floor(dimension / 8)) }}
        />
        {label && <span className={`text-sm ${textClass}`}>{label}</span>}
      </div>
    );
  };

  const getCompletionPct = (closed: number, total: number) => {
    if (!total || total <= 0) return 0;
    return Math.round((closed / total) * 100);
  };

  const exportStoresToCsv = (rows: Array<{ id: number; name: string; storenumber?: string; open: number; closed: number; total: number }>) => {
    const headers = ['Store', 'Open', 'Closed', 'Total', 'Completion %'];
    const lines = rows.map(r => {
      const total = r.open + r.closed;
      const pct = getCompletionPct(r.closed, total);
      const storeLabel = `${r.name}${r.storenumber ? ` - ${r.storenumber}` : ''}`.replaceAll(',', ' ');
      return [storeLabel, r.open, r.closed, total, `${pct}%`].join(',');
    });
    const csv = [headers.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const rcSuffix = selectedRegionalCoach?.name ? `_${selectedRegionalCoach.name.replace(/\s+/g, '_')}` : '';
    const acSuffix = selectedAreaCoach?.name ? `_${selectedAreaCoach.name.replace(/\s+/g, '_')}` : '';
    link.download = `jobcards_stores${rcSuffix}${acSuffix}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    // Fetch dashboard stats on component mount
    dispatch(fetchDashboardStatsAction());
  }, [dispatch]);

  return (
    <div className="p-4">

      <h1 className="text-2xl font-semibold text-gray-800 dark:text-white mb-6">
        Requisitions {isAdmin && <span className="text-sm font-normal text-blue-600 dark:text-blue-400">(Admin View)</span>}
      </h1>

      {/* Global Error Banner - shown if there's an error and not currently loading */}
      {dashboardError && !isLoadingDashboardStats && (
        <div className="mb-4 p-3 bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200 rounded-md shadow text-center">
          Error loading dashboard statistics: {typeof dashboardError === 'string' ? dashboardError : 'An unknown error occurred.'}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Card 1: Pending Approval */}
        <InfoCard
          label={cardContent.pendingTitle}
          gradient="from-indigo-500 to-purple-600"
          icon={
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z" />
            </svg>
          }
          value={
            isLoadingDashboardStats ? (
              <LoadingSpinner size="sm" variant="light" label="Loading..." />
            ) : dashboardError ? (
              <span className="text-white/80 text-base">Error</span>
            ) : (
              dashboardStats?.pendingMyApprovalCount ?? 'N/A'
            )
          }
        />

        {/* Card 2: Recently Approved */}
        <InfoCard
          label={cardContent.recentTitle}
          gradient="from-emerald-600 to-teal-400"
          icon={
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          }
          value={
            isLoadingDashboardStats ? (
              <LoadingSpinner size="sm" variant="light" label="Loading..." />
            ) : dashboardError ? (
              <span className="text-white/80 text-base">Error</span>
            ) : (
              dashboardStats?.recentlyApprovedCount ?? 'N/A'
            )
          }
        />

        {/* Card 3: Total Value (All Pending Submitted) */}
        <InfoCard
          label="Total Value (Pending Submitted)"
          gradient="from-amber-500 to-yellow-400"
          icon={
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-2.21 0-4 1.343-4 3s1.79 3 4 3 4 1.343 4 3-1.79 3-4 3m0-12V4m0 16v-2" />
            </svg>
          }
          value={
            isLoadingDashboardStats ? (
              <LoadingSpinner size="sm" variant="light" label="Loading..." />
            ) : dashboardError ? (
              <span className="text-white/80 text-base">Error</span>
            ) : (
              formatCurrencyWithFallback(dashboardStats?.pendingMyApprovalValue)
            )
          }
        />
      </div>

      {/* Job Cards Overview (Mock UI) */}
      <div className="mt-8 bg-white dark:bg-gray-800 shadow-lg rounded-lg p-6">
        <h2 className="text-xl font-medium text-gray-700 dark:text-gray-200 mb-4">Job Cards Overview</h2>
        {(() => {
          const userRoleId = (user as any)?.user_role_id;
          const isRegionalCoach = userRoleId === 3;
          const jobCardSummary = jcSummary ?? { total: 0, open: 0, closed: 0 };
          const regionalCoaches: Array<{ id: number; name: string; summary: { total: number; open: number; closed: number } }> = jcRegionalCoaches;
          const areaCoaches: Array<{ id: number; name: string; summary: { total: number; open: number; closed: number } }> = jcAreaCoaches;

          // Apply Store Job Cards filters (search, status, performance range)
          const filteredStoreRows = jcStores.filter((row) => {
            // Search by store name or store number
            if (storeSearchTerm.trim()) {
              const q = storeSearchTerm.trim().toLowerCase();
              const haystack = `${row.name || ''} ${row.storenumber || ''}`.toLowerCase();
              if (!haystack.includes(q)) return false;
            }
            const total = row.open + row.closed;
            const pct = getCompletionPct(row.closed, total);
            // Status filter
            if (storeStatusFilter === 'high_performers' && pct < 80) return false;
            if (storeStatusFilter === 'low_performers' && pct >= 50) return false;
            if (storeStatusFilter === 'open_heavy' && row.open <= row.closed) return false;
            if (storeStatusFilter === 'closed_heavy' && row.closed <= row.open) return false;
            // Performance range filter (based on ranking across all stores)
            if (storePerfRange !== 'all') {
              const ranked = [...jcStores].sort((a, b) => {
                const pa = getCompletionPct(a.closed, a.open + a.closed);
                const pb = getCompletionPct(b.closed, b.open + b.closed);
                return pb - pa; // descending by completion %
              });
              const idx = ranked.findIndex((r) => r.id === row.id);
              const n = ranked.length;
              if (n > 0) {
                const rankPct = (idx + 1) / n;
                if (storePerfRange === 'top_25' && rankPct > 0.25) return false;
                if (storePerfRange === 'middle_50' && (rankPct <= 0.25 || rankPct > 0.75)) return false;
                if (storePerfRange === 'bottom_25' && rankPct <= 0.75) return false;
              }
            }
            return true;
          });

          const clearStoreFilters = () => {
            setStoreSearchTerm('');
            setStoreDateRange('last_6_months');
            setStoreStartDate('');
            setStoreEndDate('');
            setStoreStatusFilter('all');
            setStorePerfRange('all');
            setStoreSortKey('pct');
            setStoreSortDir('desc');
          };

          // Toggle expansion of a store row; lazily fetch jobcards on first expand
          const toggleStoreExpand = async (storeId: number) => {
            if (expandedStoreId === storeId) {
              setExpandedStoreId(null);
              return;
            }
            setExpandedStoreId(storeId);
            // If already cached, no need to fetch
            if (storeJobcardsCache[storeId]) return;
            // Fetch jobcards for this store with the current date range
            setStoreJobcardsLoading((prev) => ({ ...prev, [storeId]: true }));
            setStoreJobcardsError((prev) => ({ ...prev, [storeId]: null }));
            try {
              const params = buildDateParams();
              const data = await fetchJson(`/approver/jobcards/store/${storeId}`, params);
              setStoreJobcardsCache((prev) => ({ ...prev, [storeId]: data?.jobcards ?? [] }));
            } catch (e: any) {
              setStoreJobcardsError((prev) => ({ ...prev, [storeId]: e?.message || 'Failed to load job cards' }));
            } finally {
              setStoreJobcardsLoading((prev) => ({ ...prev, [storeId]: false }));
            }
          };

          const sortedStoreRows = [...filteredStoreRows].sort((a, b) => {
            const totalA = a.open + a.closed;
            const totalB = b.open + b.closed;
            const pctA = getCompletionPct(a.closed, totalA);
            const pctB = getCompletionPct(b.closed, totalB);
            let cmp = 0;
            switch (storeSortKey) {
              case 'name':
                cmp = (a.name || '').localeCompare(b.name || '');
                break;
              case 'open':
                cmp = a.open - b.open;
                break;
              case 'closed':
                cmp = a.closed - b.closed;
                break;
              case 'total':
                cmp = totalA - totalB;
                break;
              case 'pct':
                cmp = pctA - pctB;
                break;
            }
            return storeSortDir === 'asc' ? cmp : -cmp;
          });

          const onSort = (key: 'name' | 'open' | 'closed' | 'total' | 'pct') => {
            if (storeSortKey === key) {
              setStoreSortDir(storeSortDir === 'asc' ? 'desc' : 'asc');
            } else {
              setStoreSortKey(key);
              setStoreSortDir('asc');
            }
          };
          const sortArrow = (key: 'name' | 'open' | 'closed' | 'total' | 'pct') => storeSortKey === key ? (storeSortDir === 'asc' ? '▲' : '▼') : '';
          return (
            <div className="space-y-6">
              {jcError && (
                <div className="p-3 rounded-md bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300">
                  {jcError}
                </div>
              )}
              {/* Row 1: Global Summary */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <InfoCard
                  label="Total"
                  gradient="from-sky-500 to-blue-600"
                  icon={
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h18v4H3zM3 9h18v12H3z" />
                    </svg>
                  }
                  value={
                    <div className="flex flex-col">
                      <span className="text-2xl sm:text-4xl font-bold">{jobCardSummary.total}</span>
                      <span className="text-white/80 text-xs">Completion {getCompletionPct(jobCardSummary.closed, jobCardSummary.total)}%</span>
                    </div>
                  }
                />
                <InfoCard
                  label="Open"
                  gradient="from-amber-500 to-yellow-500"
                  icon={
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2" />
                    </svg>
                  }
                  value={
                    <div className="flex flex-col">
                      <span className="text-2xl sm:text-4xl font-bold">{jobCardSummary.open}</span>
                      <span className="text-white/80 text-xs">Remaining {100 - getCompletionPct(jobCardSummary.closed, jobCardSummary.total)}%</span>
                    </div>
                  }
                />
                <InfoCard
                  label="Closed"
                  gradient="from-emerald-600 to-teal-400"
                  icon={
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  }
                  value={
                    <div className="flex flex-col">
                      <span className="text-2xl sm:text-4xl font-bold">{jobCardSummary.closed}</span>
                      <span className="text-white/80 text-xs">Completion {getCompletionPct(jobCardSummary.closed, jobCardSummary.total)}%</span>
                    </div>
                  }
                />
              </div>

              {isAdmin && (
                <div>
                  <button
                    type="button"
                    onClick={() => setIsRegionalCoachesOpen(v => !v)}
                    className="w-full mb-2 flex items-center justify-between px-3 py-2 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600"
                  >
                    <span className="flex items-center gap-2">
                      <span className="text-lg font-medium">Regional Coaches</span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 dark:bg-gray-600">{regionalCoaches.length}</span>
                      {selectedRegionalCoach && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 truncate max-w-[8rem] sm:max-w-[12rem]">
                          {selectedRegionalCoach.name}
                        </span>
                      )}
                    </span>
                    <span className="text-xs opacity-80">{isRegionalCoachesOpen ? 'Hide' : 'Show'}</span>
                  </button>
                  <div className={`${isRegionalCoachesOpen ? 'block' : 'hidden'}`}>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Click a Regional Coach card to filter Area Coaches and the stores table below.</p>
                    {(!jcLoading && jcError === null && regionalCoaches.length === 0) && (
                      <div className="mb-3 text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/40 border border-dashed border-gray-200 dark:border-gray-700 rounded-md p-3">
                        No Regional Coaches found.
                      </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {regionalCoaches.map((rc) => (
                        <button
                          type="button"
                          key={rc.id}
                          onClick={() => {
                            if (selectedRegionalCoach?.id === rc.id) {
                              setSelectedRegionalCoach(null);
                            } else {
                              setSelectedRegionalCoach({ id: rc.id, name: rc.name });
                              // reset area coach selection when regional changes
                              setSelectedAreaCoach(null);
                              // ensure Area Coaches section stays open for selection after regional filter
                              setIsAreaCoachesOpen(true);
                              setIsRegionalCoachesOpen(false);
                            }
                          }}
                          className={`text-left shadow-lg rounded-xl p-5 bg-white dark:bg-gray-800 border ${selectedRegionalCoach?.id === rc.id ? 'border-blue-500' : 'border-gray-100 dark:border-gray-700'} transform transition-all duration-200 hover:-translate-y-1 hover:shadow-2xl`}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="text-base font-semibold text-gray-800 dark:text-gray-200">{rc.name}</div>
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M3 7h18M3 12h18M3 17h18" />
                            </svg>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="inline-flex items-center gap-2 text-white text-xs font-medium px-3 py-1 rounded-full bg-gradient-to-r from-sky-500 to-blue-600">
                              <span className="opacity-90">Total</span>
                              <span className="text-white/90">{rc.summary.total}</span>
                            </span>
                            <span className="inline-flex items-center gap-2 text-white text-xs font-medium px-3 py-1 rounded-full bg-gradient-to-r from-amber-500 to-yellow-500">
                              <span className="opacity-90">Open</span>
                              <span className="text-white/90">{rc.summary.open}</span>
                            </span>
                            <span className="inline-flex items-center gap-2 text-white text-xs font-medium px-3 py-1 rounded-full bg-gradient-to-r from-emerald-600 to-teal-400">
                              <span className="opacity-90">Closed</span>
                              <span className="text-white/90">{rc.summary.closed}</span>
                            </span>
                            <span className="inline-flex items-center gap-2 text-white text-xs font-medium px-3 py-1 rounded-full bg-gradient-to-r from-slate-500 to-slate-600">
                              <span className="opacity-90">Completion</span>
                              <span className="text-white/90">{getCompletionPct(rc.summary.closed, rc.summary.total)}%</span>
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Row 2: Area Coaches (regional only) */}
              {(isRegionalCoach || isAdmin) && (
                <div>
                  <button
                    type="button"
                    onClick={() => setIsAreaCoachesOpen(v => !v)}
                    className="w-full mb-2 flex items-center justify-between px-3 py-2 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600"
                  >
                    <span className="flex items-center gap-2">
                      <span className="text-lg font-medium">Area Coaches</span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 dark:bg-gray-600">{areaCoaches.length}</span>
                      {selectedAreaCoach && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 truncate max-w-[8rem] sm:max-w-[12rem]">
                          {selectedAreaCoach.name}
                        </span>
                      )}
                    </span>
                    <span className="text-xs opacity-80">{isAreaCoachesOpen ? 'Hide' : 'Show'}</span>
                  </button>
                  <div className={`${isAreaCoachesOpen ? 'block' : 'hidden'}`}>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Click an Area Coach card to filter the stores table below.</p>
                    {(!jcLoading && jcError === null && areaCoaches.length === 0) && (
                      <div className="mb-3 text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/40 border border-dashed border-gray-200 dark:border-gray-700 rounded-md p-3">
                        No Area Coaches found.
                      </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {areaCoaches.map((ac) => (
                        <button
                          type="button"
                          key={ac.id}
                          onClick={() => {
                            if (selectedAreaCoach?.id === ac.id) {
                              setSelectedAreaCoach(null);
                            } else {
                              setSelectedAreaCoach({ id: ac.id, name: ac.name });
                              setIsAreaCoachesOpen(false);
                            }
                          }}
                          className={`text-left shadow-lg rounded-xl p-5 bg-white dark:bg-gray-800 border ${selectedAreaCoach?.id === ac.id ? 'border-blue-500' : 'border-gray-100 dark:border-gray-700'} transform transition-all duration-200 hover:-translate-y-1 hover:shadow-2xl`}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="text-base font-semibold text-gray-800 dark:text-gray-200">
                              {ac.name}
                            </div>
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M3 7h18M3 12h18M3 17h18" />
                            </svg>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="inline-flex items-center gap-2 text-white text-xs font-medium px-3 py-1 rounded-full bg-gradient-to-r from-sky-500 to-blue-600">
                              <span className="opacity-90">Total</span>
                              <span className="text-white/90">{ac.summary.total}</span>
                            </span>
                            <span className="inline-flex items-center gap-2 text-white text-xs font-medium px-3 py-1 rounded-full bg-gradient-to-r from-amber-500 to-yellow-500">
                              <span className="opacity-90">Open</span>
                              <span className="text-white/90">{ac.summary.open}</span>
                            </span>
                            <span className="inline-flex items-center gap-2 text-white text-xs font-medium px-3 py-1 rounded-full bg-gradient-to-r from-emerald-600 to-teal-400">
                              <span className="opacity-90">Closed</span>
                              <span className="text-white/90">{ac.summary.closed}</span>
                            </span>
                            <span className="inline-flex items-center gap-2 text-white text-xs font-medium px-3 py-1 rounded-full bg-gradient-to-r from-slate-500 to-slate-600">
                              <span className="opacity-90">Completion</span>
                              <span className="text-white/90">{getCompletionPct(ac.summary.closed, ac.summary.total)}%</span>
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Selected AC pill and clear filter */}
              {(selectedAreaCoach || selectedRegionalCoach) && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div className="inline-flex flex-wrap items-center gap-2 text-xs sm:text-sm px-3 py-1 rounded-full bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 max-w-full">
                    <span>Filtering by:</span>
                    {selectedRegionalCoach && (
                      <>
                        <span className="opacity-80">RC</span>
                        <strong className="truncate max-w-[10rem] sm:max-w-none">{selectedRegionalCoach.name}</strong>
                      </>
                    )}
                    {selectedAreaCoach && (
                      <>
                        <span className="opacity-60">•</span>
                        <span className="opacity-80">AC</span>
                        <strong className="truncate max-w-[10rem] sm:max-w-none">{selectedAreaCoach.name}</strong>
                      </>
                    )}
                  </div>
                  <button
                    className="text-xs sm:text-sm px-3 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 w-full sm:w-auto"
                    onClick={() => { setSelectedAreaCoach(null); setSelectedRegionalCoach(null); }}
                  >
                    Clear Filter
                  </button>
                </div>
              )}

              {/* Store Job Cards Filters */}
              <div className="mt-2 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
                    Date Range
                    <select value={storeDateRange} onChange={(e) => setStoreDateRange(e.target.value)} className="mt-1 w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-200 rounded-md px-3 py-1.5 text-sm">
                      <option value="this_week">This Week</option>
                      <option value="this_month">This Month</option>
                      <option value="last_3_months">Last 3 Months</option>
                      <option value="last_6_months">Last 6 Months</option>
                      <option value="this_year">This Year</option>
                      <option value="all_time">All Time</option>
                      <option value="custom">Custom Range</option>
                    </select>
                  </label>
                  {storeDateRange === 'custom' && (
                    <>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
                        Start Date
                        <input type="date" value={storeStartDate} max={storeEndDate || undefined} onChange={(e) => setStoreStartDate(e.target.value)} className="mt-1 w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-200 rounded-md px-3 py-1.5 text-sm" />
                      </label>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
                        End Date
                        <input type="date" value={storeEndDate} min={storeStartDate || undefined} onChange={(e) => setStoreEndDate(e.target.value)} className="mt-1 w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-200 rounded-md px-3 py-1.5 text-sm" />
                      </label>
                    </>
                  )}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Filters all job card stats by creation date: overview totals, coach cards, and the stores table.</p>
                {storeDatesInvalid && <p role="alert" className="text-sm text-red-600 dark:text-red-400">Choose both dates, with the end date on or after the start date.</p>}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {/* Search */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Search Stores</label>
                    <input
                      type="text"
                      placeholder="Search by name or number..."
                      value={storeSearchTerm}
                      onChange={(e) => setStoreSearchTerm(e.target.value)}
                      className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-200 rounded-md shadow-sm px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  {/* Status filter */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Status</label>
                    <select
                      value={storeStatusFilter}
                      onChange={(e) => setStoreStatusFilter(e.target.value as typeof storeStatusFilter)}
                      className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-200 rounded-md shadow-sm px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="all">All Stores</option>
                      <option value="high_performers">High Performers (≥80%)</option>
                      <option value="low_performers">Low Performers (below 50%)</option>
                      <option value="open_heavy">Open Heavy (More Open Jobs)</option>
                      <option value="closed_heavy">Closed Heavy (More Closed Jobs)</option>
                    </select>
                  </div>
                  {/* Performance range */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Performance Range</label>
                    <select
                      value={storePerfRange}
                      onChange={(e) => setStorePerfRange(e.target.value as typeof storePerfRange)}
                      className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-200 rounded-md shadow-sm px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="all">All Ranges</option>
                      <option value="top_25">Top 25%</option>
                      <option value="middle_50">Middle 50%</option>
                      <option value="bottom_25">Bottom 25%</option>
                    </select>
                  </div>
                </div>

                {/* Active Filters Summary and Clear Button */}
                <div className="flex flex-wrap items-center justify-between gap-2 bg-gray-50 dark:bg-gray-700 rounded-lg p-2">
                  <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                    <span className="font-medium">Active Filters:</span>
                    <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-full">
                      Showing {sortedStoreRows.length} of {jcStores.length} stores
                    </span>
                    {storeSearchTerm && (
                      <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 rounded-full">
                        Search: "{storeSearchTerm}"
                      </span>
                    )}
                    {storeStatusFilter !== 'all' && (
                      <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300 rounded-full">
                        {storeStatusFilter.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                      </span>
                    )}
                    {storePerfRange !== 'all' && (
                      <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300 rounded-full">
                        {storePerfRange.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                      </span>
                    )}
                    <span className="px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/50 text-indigo-800 dark:text-indigo-300 rounded-full">
                      Sort: {storeSortKey === 'pct' ? 'Completion %' : storeSortKey.replace(/\b\w/g, (l: string) => l.toUpperCase())} ({storeSortDir === 'asc' ? 'A-Z' : 'Z-A'})
                    </span>
                  </div>
                  <button
                    onClick={clearStoreFilters}
                    className="px-3 py-1 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
                  >
                    Clear All Filters
                  </button>
                </div>
              </div>

              {/* Table: Per-store breakdown */}
              <div className="overflow-x-auto rounded-md border border-gray-200 dark:border-gray-700">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-2">
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-200">Store Job Cards</div>
                  <button
                    className="text-sm px-3 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 w-full sm:w-auto"
                    onClick={() => exportStoresToCsv(sortedStoreRows)}
                  >
                    Export CSV
                  </button>
                </div>
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900/40">
                    <tr>
                      <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-8"></th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer" onClick={() => onSort('name')}>Store <span className="ml-1">{sortArrow('name')}</span></th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer" onClick={() => onSort('open')}>Open <span className="ml-1">{sortArrow('open')}</span></th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer" onClick={() => onSort('closed')}>Closed <span className="ml-1">{sortArrow('closed')}</span></th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer" onClick={() => onSort('total')}>Total <span className="ml-1">{sortArrow('total')}</span></th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer" onClick={() => onSort('pct')}>Completion % <span className="ml-1">{sortArrow('pct')}</span></th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {sortedStoreRows.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                          No stores match the current filters.
                        </td>
                      </tr>
                    ) : (
                      sortedStoreRows.map((row: { id: number; name: string; storenumber?: string; open: number; closed: number; total: number }) => {
                      const total = row.open + row.closed;
                      const pct = getCompletionPct(row.closed, total);
                      const isExpanded = expandedStoreId === row.id;
                      const jobcards = storeJobcardsCache[row.id];
                      const isLoadingJobcards = storeJobcardsLoading[row.id];
                      const jobcardsError = storeJobcardsError[row.id];
                      return (
                        <React.Fragment key={row.id}>
                          <tr
                            className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40 ${isExpanded ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                            onClick={() => toggleStoreExpand(row.id)}
                          >
                            <td className="px-2 py-2 text-center text-gray-400 dark:text-gray-500 select-none">
                              {isExpanded ? '▼' : '▶'}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 dark:text-gray-200">{row.name}{row.storenumber ? ` - ${row.storenumber}` : ''}</td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-amber-600 dark:text-amber-400">{row.open}</td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-emerald-600 dark:text-emerald-400">{row.closed}</td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-blue-600 dark:text-blue-400">{total}</td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 dark:text-gray-200">{pct}%</td>
                          </tr>
                          {isExpanded && (
                            <tr className="bg-gray-50 dark:bg-gray-900/30">
                              <td colSpan={6} className="px-4 py-3">
                                {isLoadingJobcards ? (
                                  <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                                    <LoadingSpinner size="sm" variant="dark" label="Loading job cards..." />
                                  </div>
                                ) : jobcardsError ? (
                                  <div className="text-sm text-red-600 dark:text-red-400">{jobcardsError}</div>
                                ) : !jobcards || jobcards.length === 0 ? (
                                  <div className="text-sm text-gray-500 dark:text-gray-400">No job cards in this period.</div>
                                ) : (
                                  <div className="overflow-auto max-h-72">
                                    <table className="min-w-full text-sm border border-gray-200 dark:border-gray-700 rounded">
                                      <thead className="bg-gray-100 dark:bg-gray-800 sticky top-0">
                                        <tr>
                                          <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Job #</th>
                                          <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Order #</th>
                                          <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Supplier</th>
                                          <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Date</th>
                                          <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Status</th>
                                          <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">ATR Items</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                        {jobcards.map((jc: any) => (
                                          <tr key={jc.id} className="align-top">
                                            <td className="px-3 py-1.5 whitespace-nowrap text-gray-700 dark:text-gray-200">{jc.job_number || '—'}</td>
                                            <td className="px-3 py-1.5 whitespace-nowrap text-gray-700 dark:text-gray-200">{jc.order_number || '—'}</td>
                                            <td className="px-3 py-1.5 whitespace-nowrap text-gray-700 dark:text-gray-200">{jc.supplier_name || '—'}</td>
                                            <td className="px-3 py-1.5 whitespace-nowrap text-gray-700 dark:text-gray-200">{jc.date || '—'}</td>
                                            <td className="px-3 py-1.5 whitespace-nowrap">
                                              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${jc.closed ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'}`}>
                                                {jc.closed ? 'Closed' : 'Open'}
                                              </span>
                                            </td>
                                            <td className="px-3 py-1.5 text-gray-700 dark:text-gray-200">
                                              {(!jc.atr_items || jc.atr_items.length === 0) ? (
                                                <span className="text-gray-400 dark:text-gray-500">No ATR items</span>
                                              ) : (
                                                <ul className="space-y-2">
                                                  {jc.atr_items.map((atr: any, idx: number) => (
                                                    <li key={idx} className="flex flex-col">
                                                      <span className="flex items-center gap-2 flex-wrap">
                                                        <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${atr.type === 'equipment' ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>
                                                          {atr.type === 'equipment' ? 'Equipment' : 'Asset'}
                                                        </span>
                                                        <span className="font-medium">{atr.asset}</span>
                                                        {atr.serial_number && (
                                                          <span className="text-xs text-gray-500 dark:text-gray-400">S/N: {atr.serial_number}</span>
                                                        )}
                                                      </span>
                                                      {(atr.category || atr.subcategory) && (
                                                        <span className="ml-1 text-xs text-gray-400 dark:text-gray-500">
                                                          {atr.category}{atr.category && atr.subcategory ? ' / ' : ''}{atr.subcategory}
                                                        </span>
                                                      )}
                                                      {atr.descriptions && atr.descriptions.length > 0 && (
                                                        <ul className="ml-6 list-disc text-xs text-gray-500 dark:text-gray-400">
                                                          {atr.descriptions.map((d: any, di: number) => (
                                                            <li key={di}>{typeof d === 'string' ? d : d.description}</li>
                                                          ))}
                                                        </ul>
                                                      )}
                                                    </li>
                                                  ))}
                                                </ul>
                                              )}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    }))}
                  </tbody>
                </table>
              </div>
              {jcLoading && (
                <div className="pt-3"><LoadingSpinner size="md" variant="dark" label="Loading job cards..." /></div>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default DashboardPage;
